import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../lib/logger';
import { recipesAPI, continentsAPI } from '../lib/api';
import { Navbar } from '../components/layout/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DifficultyStars } from '../components/ui/DifficultyStars';
import { toast } from 'sonner';
import {
  ChevronLeft, Plus, Trash2, Sparkles, ChefHat, Loader2
} from 'lucide-react';

const CONTINENTS = [
  { id: 'africa', name: 'Afrique' },
  { id: 'asia', name: 'Asie' },
  { id: 'europe', name: 'Europe' },
  { id: 'north_america', name: 'Amérique du Nord' },
  { id: 'south_america', name: 'Amérique du Sud' },
  { id: 'oceania', name: 'Océanie' },
];

const MEAL_TYPES = [
  { value: 'entree', label: 'Entrée' },
  { value: 'plat', label: 'Plat principal' },
  { value: 'dessert', label: 'Dessert' },
];

const COUNTRIES_BY_CONTINENT = {
  africa: ['Maroc', 'Tunisie', 'Algérie', 'Sénégal', 'Éthiopie', 'Afrique du Sud', 'Égypte', 'Nigeria', 'Kenya', "Côte d'Ivoire"],
  asia: ['Japon', 'Chine', 'Thaïlande', 'Vietnam', 'Inde', 'Corée du Sud', 'Indonésie', 'Philippines', 'Malaisie', 'Turquie'],
  europe: ['France', 'Italie', 'Espagne', 'Grèce', 'Portugal', 'Allemagne', 'Royaume-Uni', 'Belgique', 'Suisse', 'Pays-Bas'],
  north_america: ['États-Unis', 'Mexique', 'Canada', 'Cuba', 'Jamaïque', 'Haïti', 'République Dominicaine', 'Guatemala', 'Honduras', 'Costa Rica'],
  south_america: ['Brésil', 'Argentine', 'Pérou', 'Colombie', 'Chili', 'Venezuela', 'Équateur', 'Bolivie', 'Paraguay', 'Uruguay'],
  oceania: ['Australie', 'Nouvelle-Zélande', 'Fidji', 'Papouasie-Nouvelle-Guinée', 'Samoa', 'Tonga', 'Vanuatu', 'Nouvelle-Calédonie', 'Tahiti', 'Hawaï'],
};

export default function NewRecipePage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [mode, setMode] = useState('manual'); // 'manual' or 'ai'
  const [loading, setLoading] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  
  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [continent, setContinent] = useState('');
  const [country, setCountry] = useState('');
  const [mealType, setMealType] = useState('');
  const [difficulty, setDifficulty] = useState(3);
  const [prepTime, setPrepTime] = useState('');
  const [cookTime, setCookTime] = useState('');
  const [servings, setServings] = useState('4');
  const [ingredients, setIngredients] = useState([{ name: '', quantity: '', unit: '' }]);
  const [instructions, setInstructions] = useState(['']);
  const [imageUrl, setImageUrl] = useState('');
  const [tags, setTags] = useState('');

  // AI fields
  const [aiIngredients, setAiIngredients] = useState('');

  const availableCountries = continent ? COUNTRIES_BY_CONTINENT[continent] || [] : [];

  useEffect(() => {
    if (!isAuthenticated) {
      toast.error('Connectez-vous pour ajouter une recette');
      navigate('/login', { state: { from: { pathname: '/recipes/new' } } });
    }
  }, [isAuthenticated, navigate]);

  const addIngredient = () => {
    setIngredients([...ingredients, { name: '', quantity: '', unit: '' }]);
  };

  const removeIngredient = (index) => {
    if (ingredients.length > 1) {
      setIngredients(ingredients.filter((_, i) => i !== index));
    }
  };

  const updateIngredient = (index, field, value) => {
    const updated = [...ingredients];
    updated[index][field] = value;
    setIngredients(updated);
  };

  const addInstruction = () => {
    setInstructions([...instructions, '']);
  };

  const removeInstruction = (index) => {
    if (instructions.length > 1) {
      setInstructions(instructions.filter((_, i) => i !== index));
    }
  };

  const updateInstruction = (index, value) => {
    const updated = [...instructions];
    updated[index] = value;
    setInstructions(updated);
  };

  const handleGenerateAI = async () => {
    if (!country || !mealType) {
      toast.error('Sélectionnez un pays et un type de plat');
      return;
    }

    setAiGenerating(true);
    
    try {
      const ingredientsList = aiIngredients
        .split(',')
        .map(i => i.trim())
        .filter(i => i.length > 0);

      const response = await recipesAPI.generate({
        country,
        meal_type: mealType,
        ingredients: ingredientsList,
      });

      toast.success('Recette générée avec succès !');
      navigate(`/recipes/${response.data.id}`);
    } catch (error) {
      logger.error('AI generation failed:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!name || !description || !continent || !country || !mealType) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const validIngredients = ingredients.filter(i => i.name && i.quantity);
    if (validIngredients.length === 0) {
      toast.error('Ajoutez au moins un ingrédient');
      return;
    }

    const validInstructions = instructions.filter(i => i.trim());
    if (validInstructions.length === 0) {
      toast.error('Ajoutez au moins une étape de préparation');
      return;
    }

    setLoading(true);

    try {
      const recipeData = {
        name,
        description,
        continent,
        country,
        meal_type: mealType,
        difficulty,
        prep_time: parseInt(prepTime) || 15,
        cook_time: parseInt(cookTime) || 30,
        servings: parseInt(servings) || 4,
        ingredients: validIngredients,
        instructions: validInstructions,
        image_url: imageUrl,
        tags: tags.split(',').map(t => t.trim()).filter(t => t),
      };

      const response = await recipesAPI.create(recipeData);
      toast.success('Recette créée avec succès !');
      navigate(`/recipes/${response.data.id}`);
    } catch (error) {
      logger.error('Failed to create recipe:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-worldplate-cream">
      <Navbar />

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-8">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-worldplate-muted hover:text-worldplate-text mb-6 -ml-2"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Retour
        </Button>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1 className="font-heading text-4xl font-bold text-worldplate-deep mb-4">
            Ajouter une recette
          </h1>
          <p className="text-worldplate-muted">
            Partagez vos créations culinaires avec la communauté
          </p>
        </motion.div>

        {/* Mode Toggle */}
        <div className="flex gap-4 mb-8">
          <Button
            variant={mode === 'manual' ? 'default' : 'outline'}
            onClick={() => setMode('manual')}
            className={`flex-1 gap-2 ${mode === 'manual' ? 'bg-worldplate-primary' : ''}`}
            data-testid="mode-manual"
          >
            <ChefHat className="w-5 h-5" />
            Création manuelle
          </Button>
          <Button
            variant={mode === 'ai' ? 'default' : 'outline'}
            onClick={() => setMode('ai')}
            className={`flex-1 gap-2 ${mode === 'ai' ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
            data-testid="mode-ai"
          >
            <Sparkles className="w-5 h-5" />
            Génération IA
          </Button>
        </div>

        {/* AI Mode */}
        {mode === 'ai' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-2xl p-8 border border-stone-100 shadow-sm"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-semibold text-worldplate-deep">
                  Génération par IA
                </h2>
                <p className="text-sm text-worldplate-muted">
                  L'IA va créer une recette authentique pour vous
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Continent *</Label>
                  <Select value={continent} onValueChange={(v) => { setContinent(v); setCountry(''); }}>
                    <SelectTrigger className="mt-2" data-testid="ai-continent-select">
                      <SelectValue placeholder="Sélectionnez" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTINENTS.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Pays *</Label>
                  <Select value={country} onValueChange={setCountry} disabled={!continent}>
                    <SelectTrigger className="mt-2" data-testid="ai-country-select">
                      <SelectValue placeholder="Sélectionnez" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCountries.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Type de plat *</Label>
                <Select value={mealType} onValueChange={setMealType}>
                  <SelectTrigger className="mt-2" data-testid="ai-mealtype-select">
                    <SelectValue placeholder="Sélectionnez" />
                  </SelectTrigger>
                  <SelectContent>
                    {MEAL_TYPES.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Ingrédients suggérés (optionnel)</Label>
                <Input
                  value={aiIngredients}
                  onChange={(e) => setAiIngredients(e.target.value)}
                  placeholder="Ex: poulet, citron, olives (séparés par des virgules)"
                  className="mt-2"
                  data-testid="ai-ingredients-input"
                />
                <p className="text-xs text-worldplate-muted mt-1">
                  L'IA essaiera d'intégrer ces ingrédients dans la recette
                </p>
              </div>

              <Button
                onClick={handleGenerateAI}
                disabled={aiGenerating || !country || !mealType}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-xl h-12 gap-2"
                data-testid="generate-ai-btn"
              >
                {aiGenerating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Génération en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Générer la recette
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Manual Mode */}
        {mode === 'manual' && (
          <motion.form
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onSubmit={handleSubmit}
            className="space-y-8"
          >
            {/* Basic Info */}
            <div className="bg-white rounded-2xl p-8 border border-stone-100 shadow-sm space-y-6">
              <h2 className="font-heading text-xl font-semibold text-worldplate-deep">
                Informations de base
              </h2>

              <div>
                <Label htmlFor="name">Nom de la recette *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Couscous royal"
                  className="mt-2"
                  required
                  data-testid="recipe-name-input"
                />
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Décrivez votre recette en quelques phrases..."
                  className="mt-2 min-h-[100px]"
                  required
                  data-testid="recipe-description-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Continent *</Label>
                  <Select value={continent} onValueChange={(v) => { setContinent(v); setCountry(''); }}>
                    <SelectTrigger className="mt-2" data-testid="continent-select">
                      <SelectValue placeholder="Sélectionnez" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTINENTS.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Pays *</Label>
                  <Select value={country} onValueChange={setCountry} disabled={!continent}>
                    <SelectTrigger className="mt-2" data-testid="country-select">
                      <SelectValue placeholder="Sélectionnez" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCountries.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Type de plat *</Label>
                  <Select value={mealType} onValueChange={setMealType}>
                    <SelectTrigger className="mt-2" data-testid="mealtype-select">
                      <SelectValue placeholder="Sélectionnez" />
                    </SelectTrigger>
                    <SelectContent>
                      {MEAL_TYPES.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Difficulté</Label>
                  <div className="mt-3">
                    <DifficultyStars
                      difficulty={difficulty}
                      interactive={true}
                      onChange={setDifficulty}
                      size="lg"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="prepTime">Temps de préparation (min)</Label>
                  <Input
                    id="prepTime"
                    type="number"
                    value={prepTime}
                    onChange={(e) => setPrepTime(e.target.value)}
                    placeholder="15"
                    className="mt-2"
                    data-testid="prep-time-input"
                  />
                </div>
                <div>
                  <Label htmlFor="cookTime">Temps de cuisson (min)</Label>
                  <Input
                    id="cookTime"
                    type="number"
                    value={cookTime}
                    onChange={(e) => setCookTime(e.target.value)}
                    placeholder="30"
                    className="mt-2"
                    data-testid="cook-time-input"
                  />
                </div>
                <div>
                  <Label htmlFor="servings">Portions</Label>
                  <Input
                    id="servings"
                    type="number"
                    value={servings}
                    onChange={(e) => setServings(e.target.value)}
                    placeholder="4"
                    className="mt-2"
                    data-testid="servings-input"
                  />
                </div>
              </div>
            </div>

            {/* Ingredients */}
            <div className="bg-white rounded-2xl p-8 border border-stone-100 shadow-sm space-y-6">
              <h2 className="font-heading text-xl font-semibold text-worldplate-deep">
                Ingrédients
              </h2>

              <div className="space-y-3">
                {ingredients.map((ing, index) => (
                  <div key={index} className="flex gap-3 items-start">
                    <div className="flex-1 grid grid-cols-3 gap-2">
                      <Input
                        value={ing.quantity}
                        onChange={(e) => updateIngredient(index, 'quantity', e.target.value)}
                        placeholder="Quantité"
                        data-testid={`ingredient-qty-${index}`}
                      />
                      <Input
                        value={ing.unit}
                        onChange={(e) => updateIngredient(index, 'unit', e.target.value)}
                        placeholder="Unité"
                        data-testid={`ingredient-unit-${index}`}
                      />
                      <Input
                        value={ing.name}
                        onChange={(e) => updateIngredient(index, 'name', e.target.value)}
                        placeholder="Ingrédient"
                        data-testid={`ingredient-name-${index}`}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeIngredient(index)}
                      className="text-worldplate-error hover:text-worldplate-error/80"
                      data-testid={`remove-ingredient-${index}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </Button>
                  </div>
                ))}
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={addIngredient}
                className="gap-2"
                data-testid="add-ingredient-btn"
              >
                <Plus className="w-4 h-4" />
                Ajouter un ingrédient
              </Button>
            </div>

            {/* Instructions */}
            <div className="bg-white rounded-2xl p-8 border border-stone-100 shadow-sm space-y-6">
              <h2 className="font-heading text-xl font-semibold text-worldplate-deep">
                Préparation
              </h2>

              <div className="space-y-4">
                {instructions.map((step, index) => (
                  <div key={index} className="flex gap-3 items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-worldplate-primary text-white rounded-full flex items-center justify-center font-bold text-sm">
                      {index + 1}
                    </div>
                    <Textarea
                      value={step}
                      onChange={(e) => updateInstruction(index, e.target.value)}
                      placeholder={`Étape ${index + 1}...`}
                      className="flex-1 min-h-[80px]"
                      data-testid={`instruction-${index}`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeInstruction(index)}
                      className="text-worldplate-error hover:text-worldplate-error/80"
                      data-testid={`remove-instruction-${index}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </Button>
                  </div>
                ))}
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={addInstruction}
                className="gap-2"
                data-testid="add-instruction-btn"
              >
                <Plus className="w-4 h-4" />
                Ajouter une étape
              </Button>
            </div>

            {/* Optional */}
            <div className="bg-white rounded-2xl p-8 border border-stone-100 shadow-sm space-y-6">
              <h2 className="font-heading text-xl font-semibold text-worldplate-deep">
                Options supplémentaires
              </h2>

              <div>
                <Label htmlFor="imageUrl">URL de l'image (optionnel)</Label>
                <Input
                  id="imageUrl"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://..."
                  className="mt-2"
                  data-testid="image-url-input"
                />
              </div>

              <div>
                <Label htmlFor="tags">Tags (optionnel)</Label>
                <Input
                  id="tags"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="végétarien, rapide, festif (séparés par des virgules)"
                  className="mt-2"
                  data-testid="tags-input"
                />
              </div>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-worldplate-primary hover:bg-worldplate-primary/90 text-white rounded-xl h-14 text-lg font-bold"
              data-testid="submit-recipe-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Création en cours...
                </>
              ) : (
                'Publier la recette'
              )}
            </Button>
          </motion.form>
        )}
      </div>
    </div>
  );
}
